import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { Table } from '../../components/Table/Table';
import { Pagination } from '../../components/Pagination/Pagination';
import { RejectReasonDialog } from '../../components/RejectReasonDialog/RejectReasonDialog';
import { ItemNotesDialog } from '../../components/ItemNotesDialog/ItemNotesDialog';
import { ColumnManager } from '../../components/ColumnManager/ColumnManager';
import { CustomerOrderModal } from '../../components/CustomerOrder/CustomerOrder';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useCustomerOrders } from '../../hooks/orders/useCustomerOrders';
import { useCustomerOrderColumns } from '../../hooks/orders/useCustomerOrderColumns';
import { useColumnPreferences } from '../../hooks/table-preferences/useColumnPreferences';
import { ORDER_STATUS_OPTIONS } from '../../constants/orderStatus';
import { fetchAllCustomerOrdersForExport } from '../../features/customer-orders/api';
import { downloadCustomerOrdersCsv } from '../../utils/exportCsv';
import './orders.scss';

import { CustomerCreditSummary } from '../../components/CustomerCreditSummary/CustomerCreditSummary';

const TABLE_ID = 'customer-orders';

export default function OrdersPage() {
  const {
    flattenedOrders,
    total,
    totalPages,
    isLoading,
    isFetching,
    status,
    search,
    productId,
    page,
    pageSize,
    setStatus,
    setSearch,
    setProductId,
    setPage,
    setPageSize,
    cancelModal,
    notesModal,
    handleCancelRequest,
    handleCancelConfirm,
    addresses,
    isUpdatingOrder,
    handleUpdateConfirm,
    sortBy,
    sortDirection,
    handleSort,
    displayModel,
    handleShowOrder,
  } = useCustomerOrders();

  const [isExporting, setIsExporting] = React.useState(false);

  const { columns: baseColumns } = useCustomerOrderColumns({
    addresses,
    onCancelRequest: handleCancelRequest,
    onViewNotes: notesModal.open,
    onShowOrder: handleShowOrder,
  });

  const {
    visibleColumns,
    columnVisibility,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    isUpdating,
  } = useColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
  });

  const products = React.useMemo(() => {
    const productMap = new Map();

    flattenedOrders.forEach((item) => {
      if (item.product?.id && !productMap.has(item.product.id)) {
        productMap.set(item.product.id, {
          id: item.product.id,
          name: item.product.name,
        });
      }
    });

    return Array.from(productMap.values()).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [flattenedOrders]);

  const selectedDisplayRow = React.useMemo(() => {
    const selectedOrderId = displayModel.data?.orderId;

    if (!selectedOrderId) {
      return null;
    }

    return flattenedOrders.find((row) => row.orderId === selectedOrderId) ?? null;
  }, [displayModel.data, flattenedOrders]);

  const navigate = useNavigate();

  const handleDownloadCsv = async () => {
    if (isExporting || total === 0) {
      return;
    }

    setIsExporting(true);

    try {
      const orders = await fetchAllCustomerOrdersForExport({
        status,
        search,
        productId,
        sortBy,
        sortDirection,
      });

      const exportedCount = downloadCustomerOrdersCsv(orders, baseColumns);

      toast.success(
        `Downloaded ${exportedCount.toLocaleString()} ${
          exportedCount === 1 ? 'order' : 'orders'
        } to CSV.`
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || error?.message || 'Failed to download orders to CSV.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card">
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div>
            <h2 className="card__title">My Orders</h2>
            <p className="card__subtitle">
              Showing {flattenedOrders.length} of {total} total
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={handleDownloadCsv}
              disabled={total === 0 || isExporting}
              title="Download every order matching the current filters with all customer data columns"
            >
              {isExporting ? 'Exporting…' : `↓ Export CSV (${total.toLocaleString()})`}
            </button>

            <ColumnManager
              columns={baseColumns}
              columnVisibility={columnVisibility}
              onToggleColumn={toggleColumn}
              onShowAll={showAllColumns}
              onHideAll={hideAllColumns}
              isUpdating={isUpdating}
            />
          </div>
        </div>

        <CustomerCreditSummary />

        <div className="card card--bleed-top" style={{ marginBottom: 16 }}>
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            <div className="form-control">
              <label>Search</label>
              <SearchInput
                placeholder="Product, city, state, PO number…"
                value={search}
                onValueChange={setSearch}
              />
            </div>

            <div className="form-control">
              <label>Status</label>
              <select value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="">All statuses</option>
                {ORDER_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label>Product</label>
              <select value={productId} onChange={(event) => setProductId(event.target.value)}>
                <option value="">All products</option>
                {products.map((product: any) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control">
              <label>Page size</label>
              <select
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {[10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control" style={{ justifyContent: 'flex-end' }}>
              <label>&nbsp;</label>

              <button
                className="btn btn--primary"
                style={{ alignSelf: 'flex-end' }}
                onClick={() => navigate('/customer-products')}
              >
                Add Orders
              </button>
            </div>
          </div>
        </div>

        <Table
          columns={visibleColumns}
          data={flattenedOrders}
          isLoading={isLoading}
          emptyMessage="No orders yet."
          className="table--orders"
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />

        {flattenedOrders.length > 0 && (
          <div className="pager-row">
            <div />
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}

        {isFetching && (
          <div className="muted" style={{ marginTop: 8 }}>
            Refreshing…
          </div>
        )}

        <RejectReasonDialog
          isOpen={cancelModal.isOpen}
          onClose={cancelModal.close}
          onConfirm={handleCancelConfirm}
        />

        <ItemNotesDialog
          open={notesModal.isOpen}
          onClose={notesModal.close}
          item={notesModal.data}
        />
        {displayModel.isOpen && displayModel.data && (
          <CustomerOrderModal
            orderId={displayModel.data.orderId}
            onClose={displayModel.close}
            addresses={addresses}
            currentDeliveryAddressId={selectedDisplayRow?.deliveryAddressId ?? null}
            orderOwnerUserId={selectedDisplayRow?.userId ?? null}
            isSaving={isUpdatingOrder}
            onSave={async ({ deliveryAddressId, poNumber }) => {
              await handleUpdateConfirm({
                orderId: displayModel.data!.orderId,
                deliveryAddressId,
                poNumber,
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
